import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import {
  createSubAccount,
  getCompanyGroupAdmins,
  getSubCompanyAdmins,
  getAccountingAccountsWithParams,
} from '../core/_requests'
import {
  AccountingAccount,
  CompanyGroupAdmin,
  SubCompanyAdmin,
} from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface SubAccountingModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  embedded?: boolean
  stayOpenOnSubmit?: boolean
}

const SubAccountingModal: FC<SubAccountingModalProps> = ({
  visible,
  onClose,
  onSubmit,
  embedded = false,
  stayOpenOnSubmit = false,
}) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)

  const [formData, setFormData] = useState({
    company: '',
    subCompany: '',
    subaccountCodes: '',
    accountCodeId: '',
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [accountingCodes, setAccountingCodes] = useState<AccountingAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)

  // New state for company and sub company dropdowns
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<SubCompanyAdmin[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  // Load companies when modal opens (global library scope only)
  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    }
  }, [visible, libraryScopeAll])

  // Load subcompanies when company is selected (for superadmin)
  useEffect(() => {
    if (formData.company) {
      loadSubCompanies(parseInt(formData.company))
    } else {
      setSubCompanies([])
    }
  }, [formData.company])

  // Load accounting codes when modal opens or when company changes
  useEffect(() => {
    if (visible) {
      loadAccountingCodes()
    }
  }, [visible, formData.company])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadSubCompanies = async (companyId: number) => {
    try {
      const subCompaniesData = await getSubCompanyAdmins(companyId)
      setSubCompanies(subCompaniesData)
    } catch (error) {
      console.error('Error loading sub companies:', error)
      setSubCompanies([])
    }
  }

  const loadAccountingCodes = async () => {
    setIsLoadingAccounts(true)
    try {
      if (libraryScopeAll) {
        if (!formData.company) {
          setAccountingCodes([])
          setIsLoadingAccounts(false)
          return
        }
      }

      let cgaIdToUse: number | undefined
      if (libraryScopeAll && formData.company) {
        cgaIdToUse = parseInt(formData.company, 10)
      } else {
        cgaIdToUse = tenantCgaId
      }
      if (!cgaIdToUse) {
        setAccountingCodes([])
        setIsLoadingAccounts(false)
        return
      }

      const data = await getAccountingAccountsWithParams(cgaIdToUse)
      setAccountingCodes(data)
    } catch (error) {
      console.error('Error loading accounting codes:', error)
      setAccountingCodes([])
    } finally {
      setIsLoadingAccounts(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    // Company validation for superadmin
    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }

    if (!formData.subaccountCodes.trim()) newErrors.subaccountCodes = 'Sub Account Code is required'
    if (!formData.accountCodeId) newErrors.accountCodeId = 'Account Code is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetFormState = () => {
    setFormData({ company: '', subCompany: '', subaccountCodes: '', accountCodeId: '' })
    setErrors({})
    setAccountingCodes([])
    setCompanies([])
    setSubCompanies([])
    setIsSubmitting(false)
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    setIsSubmitting(true)
    try {
      let cgaIdToUse: number | undefined
      if (libraryScopeAll && formData.company) {
        cgaIdToUse = parseInt(formData.company, 10)
      } else {
        cgaIdToUse = tenantCgaId
      }
      if (!cgaIdToUse) {
        toast.error('Unable to resolve company scope for this user')
        setIsSubmitting(false)
        return
      }

      await createSubAccount({
        subaccountCodes: formData.subaccountCodes,
        accountCodeId: parseInt(formData.accountCodeId),
        cgaid: { id: cgaIdToUse },
      } as any)
      toast.success('Sub Accounting added successfully!')
      onSubmit()
      if (stayOpenOnSubmit) {
        resetFormState()
      } else {
        handleClose()
      }
    } catch (error: any) {
      console.error('Error adding sub accounting:', error)
      toast.error('Failed to add sub accounting')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    resetFormState()
    onClose()
  }

  if (!visible) return null

  const modalContent = (
    <div
      className='modal-content bg-white'
      style={{
        color: '#181C32',
        height: embedded ? '100%' : undefined,
        borderRadius: embedded ? '12px' : undefined,
        boxShadow: embedded ? '0 12px 40px rgba(15,23,42,0.06)' : undefined,
        border: embedded ? '1px solid #E9EEF5' : undefined,
      }}
    >
      <div className='modal-header'>
        <h5 className='modal-title'>Add Sub Accounting</h5>
        <button type='button' className='btn-close' onClick={handleClose}></button>
      </div>
      <div className='modal-body' style={{overflowY: embedded ? 'auto' : undefined}}>
        <div className='row g-3'>

          {/* Company dropdown - only for superadmin */}
          {libraryScopeAll && (
            <div className='col-md-6'>
              <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
              <SearchableSelect
                className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                name='company'
                value={formData.company}
                onChange={handleInputChange}
                disabled={isLoadingDropdowns}
              >
                <option value=''>Select Company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </SearchableSelect>
              {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
            </div>
          )}

          {/* Subcompany dropdown - only for superadmin when company is selected */}
          {libraryScopeAll && formData.company && (
            <div className='col-md-6'>
              <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Company</label>
              <SearchableSelect
                className='form-select'
                name='subCompany'
                value={formData.subCompany}
                onChange={handleInputChange}
              >
                <option value=''>Select Sub Company (Optional)</option>
                {subCompanies.map(subCompany => (
                  <option key={subCompany.id} value={subCompany.id}>{subCompany.name}</option>
                ))}
              </SearchableSelect>
            </div>
          )}

          <div className='col-12'>
            <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Sub Account Code</label>
            <input
              type='text'
              className={`form-control ${errors.subaccountCodes ? 'is-invalid' : ''}`}
              name='subaccountCodes'
              value={formData.subaccountCodes}
              onChange={handleInputChange}
              placeholder='e.g., 6000-EXP-01-01'
              style={{ color: '#000' }}
            />
            {errors.subaccountCodes && <div className='invalid-feedback'>{errors.subaccountCodes}</div>}
          </div>

          <div className='col-md-6'>
            <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Account Code</label>
            <SearchableSelect
              className={`form-select ${errors.accountCodeId ? 'is-invalid' : ''}`}
              name='accountCodeId'
              value={formData.accountCodeId}
              onChange={handleInputChange}
              disabled={isLoadingAccounts || (libraryScopeAll && !formData.company)}
            >
              <option value=''>
                {isLoadingAccounts ? 'Loading account codes...' :
                  (libraryScopeAll && !formData.company) ? 'Please select a company first' :
                    'Select Account Code'}
              </option>
              {accountingCodes.map(account => (
                <option key={account.id} value={account.id}>
                  {account.accountCodes}
                </option>
              ))}
            </SearchableSelect>
            {errors.accountCodeId && <div className='invalid-feedback'>{errors.accountCodeId}</div>}
          </div>
        </div>
      </div>
      <div className='modal-footer'>
        <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>Cancel</button>
        <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Sub Accounting'}
        </button>
      </div>
    </div>
  )

  if (embedded) {
    return (
      <div
        className='h-100 d-flex flex-column'
        style={{ background: '#F8FAFC', padding: '12px', overflow: 'auto' }}
      >
        {modalContent}
      </div>
    )
  }

  return (
    <div className="modal fade show d-flex align-items-center justify-content-center" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1050 }}>
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        {modalContent}
      </div>
    </div>
  )
}

export default SubAccountingModal;
